import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { NodeIndexOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import { GenericWorkflowDiagramEditor, StateVisual } from './GenericWorkflowDiagramEditor'

interface Role { id: number; nome: string }
interface RDAWorkflowTransition {
  id: number; from_status_id: number; to_status_id: number
  required_role_id: number; description?: string; conditions_json?: string
  actions_json?: string; attivo: boolean; ordine: number
}
interface RDAStatusType { id: number; nome: string; ordine: number; x?: number | null; y?: number | null }

const NODE_COLOR = '#1890ff'
const NODE_SPACING = 155
const NODE_Y = 200
const NODE_Y_SUB = 380

const getStateLabel = (nome: string) =>
  nome.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

const TRANSITIONS_URL = `${API_ENDPOINTS.SYSTEM_TABLES}/rda-workflow-transitions`
const STATES_URL = `${API_ENDPOINTS.SYSTEM_TABLES}/purchase-request-statuses-list`

export const RDAWorkflowDiagramEditor: React.FC = () => {
  const [transitions, setTransitions] = useState<RDAWorkflowTransition[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [statusTypes, setStatusTypes] = useState<RDAStatusType[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [tRes, rRes, sRes] = await Promise.all([
        axiosInstance.get(TRANSITIONS_URL),
        axiosInstance.get(`${API_ENDPOINTS.ROLES}?limit=100`),
        axiosInstance.get(STATES_URL),
      ])
      setTransitions(tRes.data.items ?? tRes.data)
      setRoles(rRes.data.items ?? rRes.data)
      setStatusTypes(sRes.data)
    } catch {
      // error handled by generic component
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Salva posizione x/y nel DB via PUT
  const handlePositionSave = useCallback(async (key: string, x: number, y: number) => {
    // key = String(status.id)
    const statusType = statusTypes.find(s => String(s.id) === key)
    if (!statusType) return
    try {
      await axiosInstance.put(
        `${API_ENDPOINTS.SYSTEM_TABLES}/purchase-request-statuses/${statusType.id}`,
        { x, y }
      )
    } catch {
      // posizione non salvata, non critico
    }
  }, [statusTypes])

  // Calcola configurazione visuale dagli stati DB.
  // ordine intero → riga principale, decimale → riga secondaria (sotto il floor)
  const { stateConfigVisual, stateOrder, stateKeys, stateSelectOptions } = useMemo(() => {
    const cfgVisual: Record<string, StateVisual> = {}
    const order: Record<string, number> = {}
    const keys: string[] = []

    const mainStates = statusTypes
      .filter(s => Number.isInteger(s.ordine))
      .sort((a, b) => a.ordine - b.ordine)
    const subStates = statusTypes
      .filter(s => !Number.isInteger(s.ordine))
      .sort((a, b) => a.ordine - b.ordine)

    // Mappa ordine intero → x sulla riga principale
    const ordineToX: Record<number, number> = {}
    mainStates.forEach((s, idx) => {
      ordineToX[s.ordine] = 75 + idx * NODE_SPACING
    })

    mainStates.forEach((s, idx) => {
      const defaultX = ordineToX[s.ordine]
      cfgVisual[String(s.id)] = {
        label: getStateLabel(s.nome),
        color: NODE_COLOR,
        icon: <NodeIndexOutlined />,
        x: s.x ?? defaultX,
        y: s.y ?? NODE_Y,
      }
      order[String(s.id)] = idx
      keys.push(String(s.id))
    })

    subStates.forEach(s => {
      const parentOrdine = Math.floor(s.ordine)
      const parentX = ordineToX[parentOrdine] ?? (75 + parentOrdine * NODE_SPACING)
      cfgVisual[String(s.id)] = {
        label: getStateLabel(s.nome),
        color: NODE_COLOR,
        icon: <NodeIndexOutlined />,
        x: s.x ?? parentX,
        y: s.y ?? NODE_Y_SUB,
      }
      order[String(s.id)] = 90
      keys.push(String(s.id))
    })

    const selectOpts = [...mainStates, ...subStates].map(s => ({
      value: s.id,
      label: getStateLabel(s.nome),
      color: NODE_COLOR,
    }))

    return { stateConfigVisual: cfgVisual, stateOrder: order, stateKeys: keys, stateSelectOptions: selectOpts }
  }, [statusTypes])

  return (
    <GenericWorkflowDiagramEditor
      stateConfig={stateConfigVisual}
      stateKeys={stateKeys}
      stateOrder={stateOrder}
      transitions={transitions}
      roles={roles}
      loading={loading}
      nodeRadius={42}
      arrowheadId="arrowhead-rda"
      loadingTip="Caricamento workflow RDO..."
      listTitle="Elenco Transizioni RDO"
      editTitle="Modifica Transizione RDO"
      createTitle="Nuova Transizione RDO"
      getFromKey={t => String(t.from_status_id)}
      getToKey={t => String(t.to_status_id)}
      transitionsUrl={TRANSITIONS_URL}
      stateSelectOptions={stateSelectOptions}
      fromFieldName="from_status_id"
      toFieldName="to_status_id"
      defaultFormValues={{ attivo: true, ordine: 0 }}
      getAddFormValues={(from, to) => ({ from_status_id: Number(from), to_status_id: Number(to), attivo: true, ordine: 0 })}
      getEditFormValues={t => ({
        from_status_id: t.from_status_id, to_status_id: t.to_status_id,
        required_role_id: t.required_role_id, description: t.description,
        conditions_json: t.conditions_json, actions_json: t.actions_json,
        attivo: t.attivo, ordine: t.ordine,
      })}
      getDupFormValues={t => ({
        from_status_id: t.from_status_id, to_status_id: t.to_status_id,
        required_role_id: undefined,
        description: t.description,
        conditions_json: t.conditions_json, actions_json: t.actions_json,
        attivo: t.attivo, ordine: t.ordine + 1,
      })}
      onRefresh={fetchData}
      onPositionSave={handlePositionSave}
    />
  )
}
